import { AfterViewInit, Component, inject } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { BehaviorSubject, Observable, combineLatest, filter, map, of, takeUntil, withLatestFrom } from "rxjs";
import { Actions, Select, Store } from "@ngxs/store";

import { DropDownItem, WebApiResponse, WebApiResponseBase } from "@app/shared/classes/general";
import { BasedComponent } from "@app/shared/components/based.component";
import { CommonModalDialogComponent } from "@app/shared/components/common-modal-dialog/common-modal-dialog.component";
import { CommonModalDialogOption, ModalDialogButtonType } from "@app/shared/components/common-modal-dialog/common-modal-dialog.constant";
import { CommonFieldName, Constants, NgxsActionName } from "@app/shared/constants";
import { LogicalOperator } from "@app/shared/enums";
import { AppDataState } from "@app/shared/stores/app-data.state";
import {
  GetSubRegions,
  GetDriverRelatedTerminalList,
  GetUserProfileMe,
  GetUserProfileRole,
  SetIsCommonDataLoaded,
  GetKpiAll,
  GetSubRegionRelatedTerminalList,
} from "@app/shared/actions/common-data.action";
import { SubRegion } from "@app/shared/classes/subRegion";
import { ImportDataResultComponent } from "./import-data-result/import-data-result.component";
import { UserProfileRole } from "@app/shared/classes/user-profile-role";
import { Terminal } from "@app/shared/classes/terminal";
import { CommonDataState } from "@app/shared/stores/common-data.state";
import { Kpi } from "@app/shared/classes/kpi";
import { UserProfile } from "@app/shared/classes/user-profile";
import { CommonPageSetting, CommonSearchCriteria, GenerateLastModifySearchCriteria } from "@app/shared/models/common-data.model";
import { Utilities } from "@app/shared/utils/utilities";
import { GetTerminalsWithoutSubregion, SetDefaultSearchCriteria, SetPageViewInitialized, SetSearchCriteriaWithLastModifyData } from "@app/shared/actions/app-data.action";
import { StateProcessorFactory } from "@app/shared/stores/state-processor/state-processor";
import { PermissionService } from "@app/shared/services/permission-service";
import { ExcelImportService } from "@app/shared/services/excel-import-service";
import { ImportResult } from "@app/shared/classes/import-result";
import { CsvService } from "@app/shared/services/csv.service";

@Component({
  selector: "app-page-based",
  template: "",
})
export abstract class PageBasedComponent extends BasedComponent implements AfterViewInit {
  store = inject(Store);
  actions$ = inject(Actions);
  dialog = inject(MatDialog);
  csvService = inject(CsvService);
  excelImportService = inject(ExcelImportService);
  permissionService = inject(PermissionService);

  functionName = "";
  isEditable$: Observable<boolean> = of(true);
  commonDataLoadErrorMessage$: Observable<string[]>;
  errorMessage$: Observable<string[]>;
  totalCount$: Observable<number>;
  currentSubRegions$: Observable<SubRegion[]>;
  currentTerminals$: Observable<Terminal[]>;
  currentTerminalDropDowns$: Observable<DropDownItem[]>;
  unMappingTerminals$: Observable<string>;

  defaultSearchCriteriaSub = new BehaviorSubject<CommonSearchCriteria[]>([]);
  defaultSearchCriteria$ = this.defaultSearchCriteriaSub.asObservable();

  @Select(CommonDataState.isCommonDataLoaded) isCommonDataLoaded$: Observable<boolean>;
  @Select(CommonDataState.profileMe) profileMe$: Observable<UserProfile>;
  @Select(CommonDataState.userCountry) userCountry$: Observable<string>;
  @Select(AppDataState.currentCommonPageSettings) commonPageSettings$: Observable<CommonPageSetting[]>;

  @Select(AppDataState.taskExecuting(NgxsActionName.GET_USER_PROFILE_ME)) userProfleLoading$: Observable<boolean>;

  @Select(
    AppDataState.tasksExecuting(
      [
        NgxsActionName.GET_USER_PROFILE_ME,
        NgxsActionName.GET_DRIVER_RELATED_TERMINAL_LIST,
        NgxsActionName.GET_SUB_REGION_RELATED_TERMINAL_LIST,
        NgxsActionName.GET_SUB_REGIONS,
        NgxsActionName.GET_KPI_ALL,
      ],
      LogicalOperator.OR
    )
  )
  commonDataLoading$: Observable<boolean>;
  @Select(
    AppDataState.tasksSuccessed(
      [
        NgxsActionName.GET_USER_PROFILE_ME,
        NgxsActionName.GET_DRIVER_RELATED_TERMINAL_LIST,
        NgxsActionName.GET_SUB_REGION_RELATED_TERMINAL_LIST,
        NgxsActionName.GET_SUB_REGIONS,
        NgxsActionName.GET_KPI_ALL,
        NgxsActionName.GET_USER_PROFILE_ROLE,
      ],
      LogicalOperator.AND
    )
  )
  commonDataLoadSuccessed$: Observable<boolean>;
  @Select(
    AppDataState.tasksError([
      NgxsActionName.GET_DRIVER_RELATED_TERMINAL_LIST,
      NgxsActionName.GET_SUB_REGION_RELATED_TERMINAL_LIST,
      NgxsActionName.GET_SUB_REGIONS,
      NgxsActionName.GET_KPI_ALL,
    ])
  )
  commonDataLoadErrorResponses$: Observable<WebApiResponseBase[]>;

  @Select(AppDataState.taskResponse(NgxsActionName.GET_DRIVER_RELATED_TERMINAL_LIST)) terminals$: Observable<Terminal[]>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_SUB_REGIONS)) subRegions$: Observable<WebApiResponse<SubRegion[]>>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_KPI_ALL)) kpiAllData$: Observable<WebApiResponse<Kpi[]>>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_USER_PROFILE_ROLE)) userProfileRole$: Observable<WebApiResponse<UserProfileRole[]>>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_TERMINALS_WITHOUT_SUBREGION)) terminalsWithoutSubregion$: Observable<Terminal[]>;

  constructor() {
    super();

    this.isEditable$ = this.profileMe$.pipe(map((profileMe) => this.permissionService.checkEditable(this.functionName, profileMe.role.role_Id)));
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.commonDataLoadErrorMessage$ = this.commonDataLoadErrorResponses$.pipe(map((errorResponses) => this.getErrorMessages(errorResponses)));

    // when no data in the store, init and get the data
    this.isCommonDataLoaded$
      .pipe(
        filter((loaded) => !loaded),
        takeUntil(this.destroy$)
      )
      .subscribe((c) =>
        this.store.dispatch([
          new GetUserProfileMe(),
          new GetDriverRelatedTerminalList(),
          new GetSubRegionRelatedTerminalList(),
          new GetSubRegions(),
          new GetKpiAll(),
          new GetUserProfileRole(),
          new GetTerminalsWithoutSubregion(),
          new SetIsCommonDataLoaded(),
        ])
      );

    this.currentSubRegions$ = this.subRegions$.pipe(
      withLatestFrom(this.userCountry$),
      map(([res, userCountry]) => res.data?.filter((item) => item.country === userCountry))
    );

    this.currentTerminals$ = this.terminals$.pipe(
      withLatestFrom(this.userCountry$),
      map(([res, userCountry]) => res?.filter((item) => item.country === userCountry))
    );

    this.currentTerminalDropDowns$ = this.currentTerminals$.pipe(
      map((currentTerminals) =>
        currentTerminals.map((item) => {
          return { label: `${item.terminalId} ${item.terminalName}`, value: item.terminalId } as DropDownItem;
        })
      )
    );

    this.unMappingTerminals$ = this.terminalsWithoutSubregion$.pipe(
      filter((t) => t !== undefined),
      withLatestFrom(this.userCountry$),
      map(([t, uc]) =>
        t
          .filter((item) => item.country === uc)
          .map((item) => item.terminalId)
          .join("; ")
      )
    );
  }

  ngAfterViewInit(): void {
    combineLatest([this.commonDataLoadSuccessed$, this.profileMe$])
      .pipe(
        filter(([a, b]) => {
          return a && b !== undefined && b !== null;
        }),
        withLatestFrom(this.subRegions$, this.terminals$, this.commonPageSettings$),
        takeUntil(this.destroy$)
      )
      .subscribe(([[isSuccessed, profileMe], subRegions, terminals, commonPageSettings]) => {
        if (Utilities.isEmptyString(this.functionName)) {
          return;
        }

        let currentCommonPageSettings = commonPageSettings.find((item) => item.functionName === this.functionName);
        if (!currentCommonPageSettings || !currentCommonPageSettings.pageSearchInitialized) {
          var currentSearchCriteria = StateProcessorFactory.GetProcessor(this.functionName).GetDefaultSearchCriteria();
          if (currentSearchCriteria.length > 0) {
            var searchCriteria = this.generateDefaultSearchCriteria(profileMe, subRegions.data, terminals);
            this.defaultSearchCriteriaSub.next([...searchCriteria, ...currentSearchCriteria]);
          }

          this.store.dispatch([new SetDefaultSearchCriteria(this.functionName, searchCriteria), new SetPageViewInitialized(this.functionName)]);
          this.onSearch();
        }
      });
  }

  onSearch = () => {};

  showResultDialog = (errorResponse: WebApiResponseBase, errorTitle: string, successMsg: string, operationAfterSuccess?: () => void) => {
    if (errorResponse && Constants.HttpResponseErrorCode.includes(errorResponse.httpResponseStatus)) {
      this.showExceptionDialog(errorResponse, errorTitle);
    } else {
      this.showSuccessDialog(successMsg, operationAfterSuccess);
    }
  };

  showExceptionDialog = (errorResponse: WebApiResponseBase, errorTitle: string) => {
    this.dialog.open(CommonModalDialogComponent, {
      data: {
        header: true,
        title: errorTitle,
        isErrorMessage: true,
        message: errorResponse.friendlyMessage_Translation_Id,
        buttonType: ModalDialogButtonType.CLOSE,
      } as CommonModalDialogOption,
      disableClose: false,
    });
  };

  showSuccessDialog = (successMsg: string, operationAfterSuccess?: () => void) => {
    const msgDialogRef = this.dialog.open(CommonModalDialogComponent, {
      data: { header: true, message: successMsg, buttonType: ModalDialogButtonType.CLOSE } as CommonModalDialogOption,
      disableClose: false,
    });

    msgDialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (operationAfterSuccess) {
          operationAfterSuccess();
        }
      });
  };

  showImportResult = (
    importResponse: ImportResult[],
    errorResponse: WebApiResponseBase,
    errorTitle: string,
    successMsg: string,
    profileMe: UserProfile,
    operationAfterSuccess?: (profileMe?: UserProfile) => void
  ) => {
    if (errorResponse && Constants.HttpResponseErrorCode.includes(errorResponse.httpResponseStatus)) {
      this.showExceptionDialog(errorResponse, errorTitle);
    } else if (importResponse && importResponse.length > 0) {
      this.showImportFailedResult(importResponse);
    } else {
      this.defaultSearchCriteriaSub.next(GenerateLastModifySearchCriteria(profileMe.apexId));
      this.store.dispatch(new SetSearchCriteriaWithLastModifyData(this.functionName));
      this.showSuccessDialog(successMsg, operationAfterSuccess);
    }
  };

  showImportFailedResult = (importResponse: ImportResult[]) => {
    this.dialog.open(ImportDataResultComponent, {
      width: "80%",
      disableClose: true,
      data: importResponse,
    });
  };

  showImportEmptyDataResult = () => {
    this.dialog.open(CommonModalDialogComponent, {
      data: { header: true, isErrorMessage: true, message: "ml_151075", buttonType: ModalDialogButtonType.CLOSE } as CommonModalDialogOption,
      disableClose: false,
    });
  };

  generateDefaultSearchCriteria = (profileMe: UserProfile, subRegions: SubRegion[], terminals: Terminal[]) => {
    var searchCriteriaList: CommonSearchCriteria[] = [];
    if (profileMe.subRegion_ids && profileMe.subRegion_ids.length > 0 && (!profileMe.terminal_ids || profileMe.terminal_ids.length === 0)) {
      var subRegionValue = subRegions.find((item) => item.subRegion_Id === profileMe.subRegion_ids[0])!.subRegionName;
      searchCriteriaList.push({
        searchBy: CommonFieldName.subRegionName,
        searchValue: [subRegionValue],
        searchValueStart: "",
        searchValueEnd: "",
      } as CommonSearchCriteria);
    } else if (profileMe.terminal_ids && profileMe.terminal_ids.length > 0) {
      var terminalValues = terminals.filter((item) => profileMe.terminal_ids.includes(item.terminal_Id)).map((item) => item.terminalId);
      searchCriteriaList.push({
        searchBy: CommonFieldName.terminalId,
        searchValue: terminalValues,
        searchValueStart: "",
        searchValueEnd: "",
      } as CommonSearchCriteria);
    }

    return searchCriteriaList;
  };

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.defaultSearchCriteriaSub.complete();
  }
}
